import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { searchBusinesses, PublicBusiness } from "../utils/publicApi";
import "./Directory.scss";

const MIN_QUERY = 2;

const Directory: React.FC = () => {
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [results, setResults] = useState<PublicBusiness[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  const canSearch = (query: string, c: string, cat: string) =>
    (query.trim().length >= MIN_QUERY) || c.trim() || cat.trim();

  const run = useCallback(async (params: { query?: string; city?: string; category?: string }) => {
    setHasSearched(true);
    const ok = canSearch(params.query || "", params.city || "", params.category || "");
    if (!ok) {
      setErr(`Tapez au moins ${MIN_QUERY} lettres (ou ajoutez une ville/catégorie).`);
      setResults([]);
      return;
    }

    setLoading(true);
    setErr(null);
    try {
      const { results } = await searchBusinesses({ ...params, page: 1, limit: 12 });
      setResults(results || []);
    } catch (e: any) {
      setErr(e?.message || "Erreur lors de la recherche.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") run({ query: q, city, category });
  };

  const reset = () => {
    setQ(""); setCity(""); setCategory("");
    setErr(null); setResults([]); setHasSearched(false);
  };

  const disabled = !canSearch(q, city, category) || loading;

  return (
    <div className="rbdir">
      <h1 className="rbdir-title">Annuaire public</h1>
      <p className="rbdir-subtitle">Recherchez une entreprise et lisez ses avis sur ReviewBoost AI.</p>

      <div className="rbdir-search">
  <div className="rbdir-fields">
    <input
      placeholder="Nom de l'entreprise (min 2 lettres)"
      value={q}
      onKeyDown={onEnter}
      onChange={(e) => setQ(e.target.value)}
    />
    <input
      placeholder="Ville"
      value={city}
      onKeyDown={onEnter}
      onChange={(e) => setCity(e.target.value)}
    />
    <input
      placeholder="Catégorie (ex: restaurant)"
      value={category}
      onKeyDown={onEnter}
      onChange={(e) => setCategory(e.target.value)}
    />
  </div>

  <div className="rbdir-actions">
    <button onClick={() => run({ query: q, city, category })} disabled={disabled}>
      {loading ? "Recherche…" : "Rechercher"}
    </button>
    <button className="rbdir-secondary" onClick={reset} disabled={loading}>
      Réinitialiser
    </button>
  </div>
</div>


      {err && <div className="rbdir-error">{err}</div>}

      <div className="rbdir-list" aria-busy={loading}>
        {!hasSearched && (
          <div className="rbdir-empty">
            Commencez par une recherche (min {MIN_QUERY} lettres) ou ajoutez une ville/catégorie.
          </div>
        )}

        {hasSearched && !loading && !err && results.length === 0 && (
          <div className="rbdir-empty">Aucun résultat pour ces filtres.</div>
        )}

        {results.map((b) => {
          const slugOrId = b.slug || b.id;
          return (
            <Link key={b.id} to={`/b/${slugOrId}`} className="rbdir-item">
              <div className="rbdir-item-title">{b.name}</div>
              {b.tagline && <div className="rbdir-item-tagline">{b.tagline}</div>}
              <div className="rbdir-item-meta">
                {b.addressCity} • {(b.categories || []).slice(0, 2).join(", ")}
              </div>
              <div className="rbdir-item-rating">
                ★ {Number(b.avgRating ?? 0).toFixed(1)}{" "}
                <span className="rbdir-item-count">{b.totalReviews || 0} avis</span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default Directory;
